import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { TeamBApi, type ObservationContext, type CareAdviceResult } from '@ai-tiaozs/shared';
import { CareTaskPanel } from '@/components/CareTaskPanel';

function readSessionObs(): { plantId?: string; observationId?: string } | null {
  const raw = sessionStorage.getItem('b_last_observation');
  if (!raw) return null;
  try {
    return JSON.parse(raw) as { plantId?: string; observationId?: string };
  } catch {
    return null;
  }
}

/**
 * B 队 · 今天要做的事(易陈)
 * 独立页:从诊断页跳转,或带 ?obsId= 打开
 */
export function TeamBCareReminder() {
  const [params] = useSearchParams();
  const obsIdFromUrl = params.get('obsId');

  const [loading, setLoading] = useState(true);
  const [context, setContext] = useState<ObservationContext | null>(null);
  const [advice, setAdvice] = useState<CareAdviceResult | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const obsId = obsIdFromUrl ?? readSessionObs()?.observationId ?? null;
      if (!obsId) {
        setContext(null);
        setAdvice(null);
        setLoading(false);
        return;
      }
      try {
        const [ctx, saved] = await Promise.all([
          TeamBApi.getObservation(obsId),
          TeamBApi.getSavedAdvice(obsId),
        ]);
        if (cancelled) return;
        setContext(ctx);
        setAdvice(saved);
      } catch {
        if (!cancelled) {
          setContext(null);
          setAdvice(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [obsIdFromUrl]);

  return (
    <div className="space-y-4">
      <header>
        <h1 className="text-lg font-bold">今天要做的事</h1>
        <p className="text-xs text-slate-500">AI 已经帮你想好了,做完点一下「我做完了」就行</p>
      </header>

      {loading && (
        <p className="flex items-center gap-1 text-xs text-slate-500">
          <Loader2 className="h-3 w-3 animate-spin" />
          加载今天的任务…
        </p>
      )}

      {!loading && (!context || !advice) && (
        <section className="card space-y-3 text-sm text-slate-600">
          <p>还没有今天的任务。</p>
          <p className="text-xs text-slate-500">需要先去选一株植物,让 AI 诊断一下,才会知道今天要做什么。</p>
          <Link to="/advice" className="btn-primary inline-flex w-full justify-center">
            去 AI 健康诊断 →
          </Link>
        </section>
      )}

      {!loading && context && advice && (
        <>
          <CareTaskPanel
            plantId={context.plantId}
            plantName={context.plant.name}
            observationId={context.observationId}
            todayAction={advice.today_action}
            reminder={advice.reminder}
            photoUrl={context.photoUrl}
            photoKey={context.photoKey}
          />
          <div className="grid grid-cols-2 gap-2">
            <Link to={`/timeline?plantId=${context.plantId}`} className="btn-outline text-center text-sm">
              养护日记
            </Link>
            <Link to={`/report?plantId=${context.plantId}`} className="btn-outline text-center text-sm">
              健康报告
            </Link>
          </div>
        </>
      )}
    </div>
  );
}
